import React, { useEffect, useState } from "react";
import { Link } from "wouter";
import Layout from "@/components/Layout";
import { SeoHead } from "@/components/SeoHead";
import { STATIC_PAGE_META } from "@/shared/staticPageMeta";
import { useAuth, type AuthUser } from "@/hooks/useAuth";
import { api, type MarketplaceStats } from "@/lib/api";
import { ContactModal } from "@/components/ContactModal";
import ArticlesPanel from "@/components/ArticlesPanel";
import { BuyListTicker } from "@/components/BuyListTicker";
import LoginModal from "@/components/LoginModal";
import TrialTermsModal from "@/components/TrialTermsModal";
import SocialMediaPanel from "@/components/SocialMediaPanel";
import pkgJson from "../../package.json";

// ── Resources panel ────────────────────────────────────────────────────────────
function ResourcesPanel({ onContact }: { onContact: () => void }) {
  const [hovered, setHovered] = React.useState<string | null>(null);
  const [podcastOpen, setPodcastOpen] = React.useState(false);

  const resources: {
    id: string;
    icon: string;
    label: string;
    sub: string;
    accent: string;
    glow: string;
    action: () => void;
    tag?: string;
    tooltip?: string;
  }[] = [
    {
      id: "quick-start",
      icon: "🚀",
      label: "Quick Start Guide",
      sub: "Set up your store in 15 minutes",
      accent: "#6d28d9",
      glow: "rgba(109,40,217,0.18)",
      tag: "Guide",
      tooltip: "Step-by-step seller setup — PDF download links are on the page",
      action: () => window.open("/quick-start", "_self"),
    },
    {
      id: "user-guide",
      icon: "📚",
      label: "Complete User Guide",
      sub: "All 18 sections · every feature",
      accent: "#e8c070",
      glow: "rgba(232,192,112,0.18)",
      tag: "Guide",
      tooltip: "Full platform documentation — PDF download links are on the page",
      action: () => window.open("/user-guide", "_self"),
    },
    {
      id: "podcast",
      icon: "🎙️",
      label: "Podcast",
      sub: "Listen to the TubeDealer story",
      accent: "#c084fc",
      glow: "rgba(192,132,252,0.18)",
      tag: "Audio",
      action: () => setPodcastOpen(o => !o),
    },
    {
      id: "video",
      icon: "▶",
      label: "Training Video",
      sub: "Walkthrough of every feature",
      accent: "#E2622E",
      glow: "rgba(226,98,46,0.18)",
      tag: "Video",
      action: () => window.open("/tubescan-video/", "_blank"),
    },
    {
      id: "infographic",
      icon: "📊",
      label: "System Overview",
      sub: "How TubeDealer scanning & marketplace connect",
      accent: "#5bbd72",
      glow: "rgba(91,189,114,0.16)",
      tag: "PDF",
      action: () => window.open("/tubescan/TubeScan_TubeDealer_Infographic.pdf", "_blank"),
    },
    {
      id: "about",
      icon: "🏢",
      label: "About Us & FAQ",
      sub: "Our story, mission & common questions",
      accent: "#38bdf8",
      glow: "rgba(56,189,248,0.16)",
      tag: "About",
      tooltip: "Our founding story, how the platform works, and answers to common buyer and seller questions",
      action: () => window.open("/about", "_self"),
    },
    {
      id: "contact",
      icon: "✉",
      label: "Contact Us",
      sub: "Questions, feedback, partnerships",
      accent: "#4caf50",
      glow: "rgba(76,175,80,0.18)",
      action: onContact,
    },
  ];

  const topResources = resources.filter(r => r.id !== "contact" && r.id !== "infographic");
  const infographicResource = resources.find(r => r.id === "infographic")!;
  const contactResource = resources.find(r => r.id === "contact")!;

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px 36px" }}>
      {/* Contact Us — standalone above resources */}
      {(() => {
        const r = contactResource;
        const isHovered = hovered === r.id;
        return (
          <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}>
            <div
              className="resource-card"
              onClick={r.action}
              onMouseEnter={() => setHovered(r.id)}
              onMouseLeave={() => setHovered(null)}
              style={{
                width: "100%",
                maxWidth: 520,
                position: "relative",
                background: isHovered ? "#111" : "#0c0c0c",
                border: `1px solid ${isHovered ? "#4caf50" : "#2a2a2a"}`,
                borderRadius: 14,
                padding: "24px 28px 22px",
                cursor: "pointer",
                transition: "border-color 0.18s, box-shadow 0.18s, transform 0.15s, background 0.15s",
                transform: isHovered ? "translateY(-2px)" : "translateY(0)",
                boxShadow: isHovered ? `0 6px 32px rgba(76,175,80,0.22), 0 2px 8px rgba(0,0,0,0.4)` : "0 1px 6px rgba(0,0,0,0.35)",
                display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center",
                gap: 20,
                userSelect: "none",
              }}
            >
              <div style={{
                width: 64, height: 64, borderRadius: 16, flexShrink: 0,
                background: isHovered ? "rgba(76,175,80,0.28)" : "rgba(76,175,80,0.1)",
                border: "1px solid #4caf5055",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 32, color: "#4caf50",
                animation: "trialBtnPulse 2.4s ease-in-out infinite",
                transition: "background 0.18s",
              }}>
                {r.icon}
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#4caf50", marginBottom: 6 }}>
                  {r.label}
                </div>
                <div style={{ fontSize: 14, color: "#888", lineHeight: 1.55 }}>{r.sub}</div>
              </div>
              <div style={{
                position: "absolute", right: 20,
                fontSize: 20, color: isHovered ? "#4caf50" : "#333", fontWeight: 700,
                transition: "color 0.18s",
              }}>
                →
              </div>
            </div>
          </div>
        );
      })()}

      {/* framed container */}
      <div style={{
        border: "1px solid #222",
        borderRadius: 16,
        padding: "20px 20px 24px",
        background: "#080808",
        boxShadow: "0 2px 16px rgba(0,0,0,0.4)",
      }}>
        <div style={{
          display: "flex", alignItems: "center",
          marginBottom: 18,
        }}>
          <h2 style={{
            margin: 0, fontSize: 15, fontWeight: 700, color: "#fff",
            textTransform: "uppercase", letterSpacing: "0.1em",
            whiteSpace: "nowrap",
          }}>
            📚 Resources
          </h2>
          <div style={{ height: 1, flex: 1, background: "#1e1e1e", marginLeft: 16 }} />
        </div>

        {/* top 5 cards */}
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 12,
          marginBottom: 12,
        }}>
          {topResources.map((r) => {
            const isHovered = hovered === r.id;
            const isPodcast = r.id === "podcast";
            return (
              <div key={r.id} style={{ display: "flex", flexDirection: "column", gap: 0, flex: "1 1 188px", maxWidth: 240, minWidth: 175 }}>
              <div
                className="resource-card"
                onClick={r.action}
                onMouseEnter={() => setHovered(r.id)}
                onMouseLeave={() => setHovered(null)}
                title={r.tooltip}
                style={{
                  background: isHovered ? "#111" : "#0c0c0c",
                  border: `1px solid ${isHovered ? r.accent : "#1c1c1c"}`,
                  borderRadius: podcastOpen && isPodcast ? "12px 12px 0 0" : 12,
                  padding: "16px 16px 14px",
                  cursor: "pointer",
                  transition: "border-color 0.18s, box-shadow 0.18s, transform 0.15s, background 0.15s",
                  transform: isHovered ? "translateY(-3px)" : "translateY(0)",
                  boxShadow: isHovered ? `0 6px 28px ${r.glow}, 0 2px 8px rgba(0,0,0,0.4)` : "0 1px 4px rgba(0,0,0,0.3)",
                  display: "flex", flexDirection: "column", gap: 10,
                  userSelect: "none",
                }}
              >
                {/* top row: icon + tag */}
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 12,
                    background: isHovered ? `${r.glow}` : "#141414",
                    border: `1px solid ${isHovered ? r.accent + "40" : "#222"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: r.id === "video" ? 20 : 24,
                    transition: "background 0.18s, border-color 0.18s",
                    color: r.id === "video" ? r.accent : "inherit",
                    fontWeight: r.id === "video" ? 700 : undefined,
                  }}>
                    {r.icon}
                  </div>
                  {r.tag && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
                      padding: "2px 7px", borderRadius: 20,
                      background: isHovered ? r.glow : "#161616",
                      color: isHovered ? r.accent : "#444",
                      border: `1px solid ${isHovered ? r.accent + "40" : "#222"}`,
                      transition: "all 0.18s",
                    }}>
                      {r.tag}
                    </span>
                  )}
                </div>

                {/* label + sub */}
                <div>
                  <div style={{
                    fontSize: 14, fontWeight: 700,
                    color: isHovered ? r.accent : "#ddd",
                    transition: "color 0.18s",
                    marginBottom: 3,
                  }}>
                    {r.label}
                  </div>
                  <div style={{ fontSize: 11, color: "#555", lineHeight: 1.45 }}>
                    {r.sub}
                  </div>
                </div>

                {/* arrow indicator */}
                <div style={{
                  fontSize: 11, color: isHovered ? r.accent : "#333",
                  fontWeight: 600, transition: "color 0.18s",
                  display: "flex", alignItems: "center", gap: 4,
                }}>
                  {isPodcast
                    ? (podcastOpen ? "▲ Collapse" : "▼ Play episode")
                    : "Open →"}
                </div>
              </div>

              {/* inline podcast player */}
              {isPodcast && podcastOpen && (
                <div style={{
                  background: "#0c0c0c",
                  border: "1px solid #c084fc44",
                  borderTop: "none",
                  borderRadius: "0 0 12px 12px",
                  padding: "12px 14px 14px",
                }}>
                  <audio
                    controls
                    autoPlay
                    preload="metadata"
                    src="/api/media/podcast"
                    style={{ width: "100%", height: 36, accentColor: "#c084fc", display: "block" }}
                  />
                </div>
              )}
            </div>
            );
          })}
        </div>

        {/* System Overview — solo centred inside resources box */}
        {(() => {
          const r = infographicResource;
          const isHovered = hovered === r.id;
          return (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div
                className="resource-card"
                onClick={r.action}
                onMouseEnter={() => setHovered(r.id)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  width: 220,
                  background: isHovered ? "#111" : "#0c0c0c",
                  border: `1px solid ${isHovered ? r.accent : "#1c1c1c"}`,
                  borderRadius: 12,
                  padding: "16px 20px 14px",
                  cursor: "pointer",
                  transition: "border-color 0.18s, box-shadow 0.18s, transform 0.15s, background 0.15s",
                  transform: isHovered ? "translateY(-3px)" : "translateY(0)",
                  boxShadow: isHovered ? `0 6px 28px ${r.glow}, 0 2px 8px rgba(0,0,0,0.4)` : "0 1px 4px rgba(0,0,0,0.3)",
                  display: "flex", flexDirection: "column", alignItems: "center",
                  gap: 10, textAlign: "center",
                  userSelect: "none",
                }}
              >
                <div style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: isHovered ? r.glow : "#141414",
                  border: `1px solid ${isHovered ? r.accent + "40" : "#222"}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 24, transition: "background 0.18s, border-color 0.18s",
                }}>
                  {r.icon}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: isHovered ? r.accent : "#ddd", transition: "color 0.18s", marginBottom: 3 }}>
                    {r.label}
                  </div>
                  <div style={{ fontSize: 11, color: "#555", lineHeight: 1.45 }}>{r.sub}</div>
                </div>
                <div style={{ fontSize: 11, color: isHovered ? r.accent : "#333", fontWeight: 600, transition: "color 0.18s" }}>
                  Open →
                </div>
              </div>
            </div>
          );
        })()}

        <SocialMediaPanel />

        {/* Newsletter signup + version — inside the resources box */}
        <div style={{ marginTop: 20, borderTop: "1px solid #1a1a1a", paddingTop: 20 }}>
          <NewsletterSignup />
        </div>

        <div style={{ textAlign: "center", paddingTop: 12, fontFamily: "monospace", fontSize: 11, color: "#999", fontWeight: 600, letterSpacing: "0.05em" }}>
          v{pkgJson.version}
        </div>
      </div>{/* end framed container */}

    </div>
  );
}

// ── Three-panel section ────────────────────────────────────────────────────────
function PanelSection({ stats }: { stats: MarketplaceStats | null }) {
  const { user } = useAuth();
  const [showTrialTerms, setShowTrialTerms] = useState(false);
  const [showContact,    setShowContact]    = useState(false);
  const [loginOpen,      setLoginOpen]      = useState(false);

  const panelBase: React.CSSProperties = {
    background: "#0f0f0f",
    border: "1px solid #1a1a1a",
    borderRadius: 16,
    padding: "28px 24px",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    flex: 1,
    minWidth: 240,
  };

  const icon = (emoji: string, bg: string) => (
    <div style={{
      width: 52, height: 52, borderRadius: 14,
      background: bg, display: "flex", alignItems: "center",
      justifyContent: "center", fontSize: 24, marginBottom: 4,
    }}>{emoji}</div>
  );

  const badge = (text: string, color: string, bg: string) => (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 20,
      background: bg, color, fontSize: 11, fontWeight: 600,
    }}>{text}</span>
  );

  const divider = <div style={{ height: 1, background: "#1a1a1a", margin: "4px 0" }} />;

  return (
    <>
      {showTrialTerms && (
        <TrialTermsModal onClose={() => setShowTrialTerms(false)} />
      )}

      <div style={{
        display: "flex", gap: 14, flexWrap: "wrap", alignItems: "stretch",
        padding: "32px 20px 16px", maxWidth: 1100, margin: "0 auto",
      }}>

        {/* ── Panel 1: TubeScan App ─────────────────────────────────── */}
        <div style={{
          ...panelBase,
          borderColor: "#4caf50",
          borderWidth: 2,
          background: "#0a0f0a",
          boxShadow: "0 0 28px rgba(76,175,80,0.12)",
        }}>
          <div title="TubeScan — scan, identify & price audio valves" style={{ width: 160, height: 160, background: "#fff", borderRadius: 14, marginBottom: 18, display: "flex", alignItems: "center", justifyContent: "center", padding: 12, boxSizing: "border-box", border: "4px solid #4caf50", boxShadow: "0 0 0 5px rgba(76,175,80,0.3), 0 0 32px rgba(76,175,80,0.65)" }}>
            {/* LCP candidate — above the fold, no lazy, highest fetch priority */}
            <picture>
              <source srcSet={`${import.meta.env.BASE_URL}icon-scan.avif`} type="image/avif" />
              <source srcSet={`${import.meta.env.BASE_URL}icon-scan.webp`} type="image/webp" />
              <img
                src={`${import.meta.env.BASE_URL}icon-scan.png`}
                alt="Scan & OCR"
                width={312}
                height={325}
                fetchPriority="high"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </picture>
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <h2 style={{ fontSize: 17, fontWeight: 800, color: "#fff", margin: 0 }}>
                  Scan &amp; Manage Tubes
                </h2>
                {badge("5-Day Free Trial", "#4caf50", "#0a1a0a")}
              </div>
              <p style={{ color: "#999", fontSize: 15, lineHeight: 1.65, margin: 0 }}>
                The complete valve scanning tool. Identify markings, look up current market prices, and add scan results directly to your Inventory System — all from your phone or desktop.
              </p>
            </div>

            {divider}

            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
              {[
                "Camera, photo & video scanner",
                "Live market sold-price lookup",
                "Send scan results to Inventory System",
                "GPS-tagged scan history",
                "$30/month · 500 scans included",
              ].map((item) => (
                <li key={item} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#888" }}>
                  <span style={{ color: "#4caf50", flexShrink: 0, marginTop: 1 }}>✓</span>
                  {item}
                </li>
              ))}
            </ul>

            {!user && (
              <p style={{ color: "#4caf50", fontSize: 11, textAlign: "center", margin: 0 }}>
                No credit card required for the 5-day trial
              </p>
            )}
          </div>

          <div style={{ paddingTop: 8 }}>
            {user ? (
              <a href="/tubescan/" style={{ textDecoration: "none" }}>
                <button style={{
                  width: "100%", padding: "11px 0", background: "#4caf50", color: "#0a0a0a",
                  border: "none", borderRadius: 9, fontWeight: 700, fontSize: 14,
                  cursor: "pointer", fontFamily: "inherit",
                }}>
                  Tube Scan ↗
                </button>
              </a>
            ) : (
              <button
                onClick={() => setShowTrialTerms(true)}
                style={{
                  width: "100%", padding: "11px 0", background: "transparent", color: "#4caf50",
                  border: "2px solid #4caf50", borderRadius: 9, fontWeight: 700, fontSize: 14,
                  cursor: "pointer", fontFamily: "inherit",
                  animation: "trialBtnPulse 2.4s ease-in-out infinite",
                }}
              >
                Start Free Trial →
              </button>
            )}
          </div>
        </div>

        {/* ── Panel 1.5: Inventory System ───────────────────────────── */}
        <div style={{ ...panelBase, borderColor: "#6d28d9", borderWidth: 2, background: user ? "#0c0a14" : "#0f0f0f", boxShadow: "0 0 28px rgba(109,40,217,0.12)" }}>
          <div
            title="Inventory System — manage stock, build offers & run your POS"
            style={{ width: 160, height: 160, background: "#fff", borderRadius: 14, marginBottom: 18, display: "flex", alignItems: "center", justifyContent: "center", padding: 10, boxSizing: "border-box", border: "4px solid #6d28d9", boxShadow: "0 0 0 5px rgba(109,40,217,0.3), 0 0 32px rgba(109,40,217,0.65)" }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7, width: "100%" }}>
              {([["📋","Stock"],["💰","Offers"],["🛒","POS"],["📊","Reports"]] as [string,string][]).map(([em, lbl]) => (
                <div key={lbl} style={{ background: "#ede9fe", borderRadius: 8, padding: "8px 4px", display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                  <span style={{ fontSize: 20 }}>{em}</span>
                  <span style={{ fontSize: 8, color: "#5b21b6", fontWeight: 800, letterSpacing: "0.03em" }}>{lbl}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <h2 style={{ fontSize: 17, fontWeight: 800, color: "#a78bfa", margin: 0 }}>
                  Inventory System
                </h2>
                {badge("Subscriber", "#a78bfa", "#0f0a1a")}
              </div>
              <p style={{ color: "#999", fontSize: 15, lineHeight: 1.65, margin: 0 }}>
                The complete hub for managing your collection. Track stock, build sales offers, run a point-of-sale at swap meets, and connect your inventory to the TubeDealer marketplace.
              </p>
            </div>

            {divider}

            <div style={{ background: "#0d0a14", border: "1px solid #2a1550", borderRadius: 8, padding: "10px 12px" }}>
              <div style={{ fontSize: 11, color: "#4a2080", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>
                What's inside
              </div>
              {[
                "Track items by brand, grade & value",
                "Sales offers & PDF quotes",
                "Swap-meet POS (cash & PayPal)",
                "Retube sets & gear matching",
                "Export inventory to CSV",
              ].map(item => (
                <div key={item} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#888", marginBottom: 5 }}>
                  <span style={{ color: "#a78bfa", flexShrink: 0, marginTop: 1 }}>✓</span>
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div style={{ paddingTop: 8 }}>
            {user ? (
              <a href="/tubescan/" style={{ textDecoration: "none" }}>
                <button style={{
                  width: "100%", padding: "11px 0", background: "#6d28d9", color: "#fff",
                  border: "none", borderRadius: 9, fontWeight: 700, fontSize: 14,
                  cursor: "pointer", fontFamily: "inherit",
                }}>
                  Open Inventory System ↗
                </button>
              </a>
            ) : (
              <button
                onClick={() => setLoginOpen(true)}
                style={{
                  width: "100%", padding: "11px 0", background: "transparent", color: "#a78bfa",
                  border: "2px solid #6d28d9", borderRadius: 9, fontWeight: 700, fontSize: 14,
                  cursor: "pointer", fontFamily: "inherit",
                  animation: "invBtnPulse 2.4s ease-in-out infinite",
                }}
              >
                Sign In to Access →
              </button>
            )}
          </div>
        </div>

        {/* ── Panel 2: Dealer Storefront (add-on) ───────────────────── */}
        <div style={{ ...panelBase, borderColor: "#e8c070", borderWidth: 2, background: user ? "#080f08" : "#0f0f0f", boxShadow: "0 0 28px rgba(232,192,112,0.12)" }}>
          {user ? (
            <>
              {/* Preview My Store — yellow card, same height as images in panels 1 & 3 */}
              <a href={`/tubescan/shop/${user.username}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                <div style={{
                  height: 160, borderRadius: 14, marginBottom: 18,
                  background: "#120e00",
                  border: "2px solid #f5c800",
                  boxShadow: "0 0 22px rgba(245,200,0,0.25)",
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", gap: 10,
                  cursor: "pointer", transition: "box-shadow 0.2s ease",
                }}
                  onMouseEnter={e => (e.currentTarget as HTMLElement).style.boxShadow = "0 0 36px rgba(245,200,0,0.45)"}
                  onMouseLeave={e => (e.currentTarget as HTMLElement).style.boxShadow = "0 0 22px rgba(245,200,0,0.25)"}
                >
                  <span style={{ fontSize: 36 }}>🏪</span>
                  <span style={{
                    fontSize: 13, fontWeight: 800, color: "#f5c800",
                    letterSpacing: "0.04em", textTransform: "uppercase",
                  }}>
                    Preview My Store ↗
                  </span>
                  <span style={{ fontSize: 11, color: "#7a6010" }}>
                    {`/shop/${user.username}`}
                  </span>
                </div>
              </a>

              {/* Title — always "List & Sell Your Tubes" in gold */}
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <h2 style={{ fontSize: 17, fontWeight: 800, color: "#F59E0B", margin: 0 }}>
                      List &amp; Sell Your Tubes
                    </h2>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: "50%", background: "#1a2010",
                      border: "2px solid #3a4a10", display: "flex", alignItems: "center",
                      justifyContent: "center", fontSize: 16, fontWeight: 800, color: "#e8c070", flexShrink: 0,
                    }}>
                      {(user.companyName ?? user.username).charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#ccc" }}>
                        {user.companyName ?? user.username}
                      </div>
                      <div style={{ fontSize: 11, color: "#444" }}>@{user.username}</div>
                    </div>
                  </div>
                </div>

                {divider}

                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontSize: 12, color: "#555", textTransform: "uppercase", letterSpacing: "0.07em" }}>Your Storefront</div>
                  {[
                    { label: "Status", value: "Active", color: "#5bbd72" },
                    { label: "Shop URL", value: `/shop/${user.username}`, color: "#888" },
                  ].map(row => (
                    <div key={row.label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                      <span style={{ color: "#555" }}>{row.label}</span>
                      <span style={{ color: row.color, fontFamily: row.label === "Shop URL" ? "monospace" : "inherit", fontSize: row.label === "Shop URL" ? 11 : 13 }}>{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ paddingTop: 8 }}>
                <Link href="/seller">
                  <button style={{
                    width: "100%", padding: "11px 0", background: "#e8c070", color: "#0a0a0a",
                    border: "none", borderRadius: 9, fontWeight: 700, fontSize: 14,
                    cursor: "pointer", fontFamily: "inherit",
                  }}>
                    Manage My Storefront ↗
                  </button>
                </Link>
              </div>
            </>
          ) : (
            <>
              <div title="List & Sell — activate your public dealer storefront" style={{ width: 160, height: 160, background: "#fff", borderRadius: 14, marginBottom: 18, display: "flex", alignItems: "center", justifyContent: "center", padding: 12, boxSizing: "border-box", border: "4px solid #e8c070", boxShadow: "0 0 0 5px rgba(232,192,112,0.3), 0 0 32px rgba(232,192,112,0.65)" }}>
                {/* Above the fold — no lazy loading */}
                <picture>
                  <source srcSet={`${import.meta.env.BASE_URL}icon-list-sell.avif`} type="image/avif" />
                  <source srcSet={`${import.meta.env.BASE_URL}icon-list-sell.webp`} type="image/webp" />
                  <img
                    src={`${import.meta.env.BASE_URL}icon-list-sell.png`}
                    alt="List & Sell"
                    width={331}
                    height={308}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                </picture>
              </div>

              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <h2 style={{ fontSize: 17, fontWeight: 800, color: "#F59E0B", margin: 0 }}>
                      List & Sell Your Tubes
                    </h2>
                  </div>
                  <p style={{ color: "#999", fontSize: 15, lineHeight: 1.65, margin: 0 }}>
                    Already a TubeDealer subscriber? Activate your branded public storefront on TubeDealer.com — an additional feature included with your subscription.
                  </p>
                </div>

                {divider}

                <div style={{ background: "#0d0d08", border: "1px solid #2a2500", borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ fontSize: 11, color: "#5a5020", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>
                    Included with your subscription
                  </div>
                  {[
                    "Your own branded storefront page",
                    "Control which items are publicly listed",
                    "Gated buyer contact & verification system",
                  ].map(item => (
                    <div key={item} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#888", marginBottom: 5 }}>
                      <span style={{ color: "#e8c070", flexShrink: 0, marginTop: 1 }}>✓</span>
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ paddingTop: 8 }}>
                <button
                  onClick={() => setLoginOpen(true)}
                  style={{
                    width: "100%", padding: "11px 0",
                    background: "#e8c070", color: "#0a0a0a",
                    border: "none", borderRadius: 9, fontWeight: 700, fontSize: 14,
                    cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  Sign In to Sell →
                </button>
              </div>
            </>
          )}

        </div>

        {/* ── Panel 3: Shop for Tubes ────────────────────────────────── */}
        <div style={{ ...panelBase, borderColor: "#7bb8e8", borderWidth: 2, background: "#090909", boxShadow: "0 0 28px rgba(123,184,232,0.12)" }}>
          <div title="Shop for Tubes — browse the marketplace" style={{ width: 160, height: 160, background: "#fff", borderRadius: 14, marginBottom: 18, display: "flex", alignItems: "center", justifyContent: "center", padding: 12, boxSizing: "border-box", border: "4px solid #7bb8e8", boxShadow: "0 0 0 5px rgba(123,184,232,0.3), 0 0 32px rgba(123,184,232,0.65)" }}>
            {/* Above the fold — no lazy loading */}
            <picture>
              <source srcSet={`${import.meta.env.BASE_URL}icon-shop.avif`} type="image/avif" />
              <source srcSet={`${import.meta.env.BASE_URL}icon-shop.webp`} type="image/webp" />
              <img
                src={`${import.meta.env.BASE_URL}icon-shop.png`}
                alt="Shop for Tubes"
                width={338}
                height={310}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </picture>
          </div>

          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <h2 style={{ fontSize: 17, fontWeight: 800, color: "#fff", margin: 0 }}>
                  Shop for Tubes
                </h2>
                {badge("Free to Browse", "#7bb8e8", "#0a0a1a")}
              </div>
              <p style={{ color: "#999", fontSize: 15, lineHeight: 1.65, margin: 0 }}>
                Browse modern production, vintage, and NOS audio valves from verified dealers. No account required to shop.
              </p>
            </div>

            {divider}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {[
                { value: stats?.sellerCount ?? "—", label: "Active Sellers", color: "#e8c070" },
                { value: stats ? stats.listingCount.toLocaleString() : "—", label: "Tubes Listed", color: "#5bbd72" },
                { value: stats ? `$${Math.round(stats.totalValue).toLocaleString()}` : "—", label: "Total Value", color: "#7bb8e8" },
                { value: "Free", label: "To Browse", color: "#555" },
              ].map(s => (
                <div key={s.label} style={{ background: "#0f0f0f", border: "1px solid #1a1a1a", borderRadius: 10, padding: "10px 12px" }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: s.color, lineHeight: 1 }}>{String(s.value)}</div>
                  <div style={{ fontSize: 11, color: "#444", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ paddingTop: 8 }}>
            <Link href="/browse">
              <button style={{
                width: "100%", padding: "11px 0", background: "transparent", color: "#7bb8e8",
                border: "2px solid #7bb8e8", borderRadius: 9, fontWeight: 700, fontSize: 14,
                cursor: "pointer", fontFamily: "inherit",
              }}>
                Browse All Tubes →
              </button>
            </Link>
          </div>
        </div>
      </div>

      {/* ── Resources Panel ────────────────────────────────────────────── */}
      <ResourcesPanel onContact={() => setShowContact(true)} />
      {showContact && <ContactModal onClose={() => setShowContact(false)} />}
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
    </>
  );
}

// ── Welcome dashboard (shown when signed in) ──────────────────────────────────
function WelcomeDashboard({ user }: { user: AuthUser }) {
  const actions = [
    { icon: "🔬", label: "Tube Scan",      href: "/tubescan/",       color: "#4caf50", external: true  },
    { icon: "🏪", label: "My Storefront", href: "/seller",           color: "#e8c070", external: false },
    { icon: "🛒", label: "Browse Tubes",  href: "/browse",           color: "#7bb8e8", external: false },
    { icon: "🎫", label: "Support",       href: "/support",          color: "#a78bfa", external: false },
    ...(user.isAdmin
      ? [{ icon: "⚙️", label: "Admin Panel", href: "/admin/support", color: "#f87171", external: false }]
      : []),
  ] as const;

  return (
    <div style={{ background: "#0c0c0c", borderBottom: "1px solid #1a1a1a", padding: "18px 20px 16px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>

        {/* Welcome header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <div style={{
            width: 38, height: 38, borderRadius: "50%",
            background: user.isAdmin ? "#1a0808" : "#1a1500",
            border: `2px solid ${user.isAdmin ? "#4a1010" : "#3a2e0f"}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 15, fontWeight: 800,
            color: user.isAdmin ? "#f87171" : "#e8c070",
            flexShrink: 0,
          }}>
            {(user.companyName ?? user.username).charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 800, color: "#fff", margin: "0 0 2px" }}>
              TubeDealer Vacuum Tube Marketplace
            </h1>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#ddd" }}>
              Welcome back, {user.companyName ?? user.username}!
            </div>
            <div style={{ fontSize: 12, color: "#555", display: "flex", alignItems: "center", gap: 6 }}>
              @{user.username}
              {user.isAdmin && (
                <span style={{
                  background: "#2a0808", color: "#f87171",
                  fontSize: 10, fontWeight: 700,
                  padding: "1px 7px", borderRadius: 10, letterSpacing: "0.06em",
                }}>
                  ADMIN
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Quick-action cards */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {actions.map(a => {
            const card = (
              <div
                style={{
                  display: "flex", alignItems: "center", gap: 7,
                  padding: "9px 15px",
                  background: "#111",
                  border: `1px solid ${a.color}33`,
                  borderRadius: 10, cursor: "pointer",
                  transition: "background 0.15s, border-color 0.15s",
                }}
                onMouseEnter={e => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.background = `${a.color}18`;
                  el.style.borderColor = a.color;
                }}
                onMouseLeave={e => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.background = "#111";
                  el.style.borderColor = `${a.color}33`;
                }}
              >
                <span style={{ fontSize: 15 }}>{a.icon}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: a.color, whiteSpace: "nowrap" }}>
                  {a.label}
                </span>
              </div>
            );
            return a.external ? (
              <a key={a.label} href={a.href} style={{ textDecoration: "none" }}>{card}</a>
            ) : (
              <Link key={a.label} href={a.href} style={{ textDecoration: "none" }}>{card}</Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Newsletter signup section ──────────────────────────────────────────────────
function NewsletterSignup() {
  const [email,     setEmail]     = useState("");
  const [name,      setName]      = useState("");
  const [submitting,setSubmitting] = useState(false);
  const [done,      setDone]      = useState(false);
  const [error,     setError]     = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) { setError("Please enter your email address."); return; }
    setSubmitting(true);
    try {
      const res  = await fetch("/api/newsletter/subscribe", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email: email.trim(), name: name.trim() || undefined }),
      });
      const data = await res.json() as { ok?: boolean; error?: string };
      if (res.ok && data.ok) {
        setDone(true);
      } else {
        setError(data.error ?? "Something went wrong — please try again.");
      }
    } catch {
      setError("Network error — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ borderTop: "1px solid #1a1a1a", padding: "40px 20px" }}>
      <div style={{ maxWidth: 520, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontSize: 22, marginBottom: 6 }}>📬</div>
        <h2 style={{ fontSize: 18, fontWeight: 800, color: "#fff", margin: "0 0 8px" }}>
          Stay in the Loop
        </h2>
        <p style={{ fontSize: 13, color: "#666", lineHeight: 1.65, margin: "0 0 24px" }}>
          Subscribe to the TubeDealer Newsletter — monthly market insights, featured finds, and community news.
        </p>

        {done ? (
          <div style={{
            background: "#0a1a0a", border: "1px solid #1f4d1f", borderRadius: 12,
            padding: "18px 24px", fontSize: 14, color: "#4ade80",
          }}>
            ✅ You're subscribed! Check your inbox for a welcome email.
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <label htmlFor="newsletter-name" className="sr-only">Your name (optional)</label>
            <input
              id="newsletter-name"
              type="text"
              placeholder="Your name (optional)"
              aria-label="Your name (optional)"
              value={name}
              onChange={e => setName(e.target.value)}
              style={{
                background: "#111", border: "1px solid #2a2a2a", color: "#ddd",
                borderRadius: 8, padding: "10px 14px", fontSize: 14,
                outline: "none", width: "100%", boxSizing: "border-box",
              }}
            />
            <label htmlFor="newsletter-email" className="sr-only">Email address</label>
            <input
              id="newsletter-email"
              type="email"
              placeholder="your@email.com"
              aria-label="Email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              style={{
                background: "#111", border: "1px solid #2a2a2a", color: "#ddd",
                borderRadius: 8, padding: "10px 14px", fontSize: 14,
                outline: "none", width: "100%", boxSizing: "border-box",
              }}
            />
            <button
              type="submit"
              disabled={submitting}
              style={{
                background: submitting ? "#1a1a1a" : "#e8c070", color: "#000",
                border: "none", borderRadius: 8, padding: "12px 20px",
                fontWeight: 700, fontSize: 14, cursor: submitting ? "not-allowed" : "pointer",
                width: "100%",
              }}
            >
              {submitting ? "…" : "Subscribe"}
            </button>
            {error && (
              <div style={{ fontSize: 12, color: "#f87171", textAlign: "center" }}>{error}</div>
            )}
            <p style={{ fontSize: 11, color: "#666", margin: 0, textAlign: "center" }}>
              No spam. Unsubscribe anytime with one click.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Semantic explainer + FAQ — improves text-to-HTML ratio for crawlers ────────
function HomeSeoSection() {
  const heading: React.CSSProperties = {
    fontSize: 15,
    fontWeight: 700,
    color: "#e8c070",
    margin: "0 0 6px",
    letterSpacing: "0.02em",
  };
  const body: React.CSSProperties = {
    fontSize: 14,
    color: "#888",
    lineHeight: 1.75,
    margin: "0 0 18px",
  };
  const faqItem: React.CSSProperties = {
    borderTop: "1px solid #1e1e1e",
    padding: "12px 0",
  };
  const summary: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 600,
    color: "#ccc",
    cursor: "pointer",
    listStyle: "none",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    userSelect: "none",
  };
  const answer: React.CSSProperties = {
    fontSize: 13,
    color: "#888",
    lineHeight: 1.75,
    marginTop: 8,
    paddingRight: 12,
  };

  return (
    <section
      aria-label="About TubeDealer"
      style={{
        borderTop: "1px solid #1a1a1a",
        padding: "40px 20px 12px",
        maxWidth: 1100,
        margin: "0 auto",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "0 48px",
        }}
      >
        {/* Left column — editorial copy */}
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: "#fff", margin: "0 0 16px" }}>
            A Better Home for Vintage Audio Components
          </h2>
          <p style={{ color: "#666", fontSize: 12, margin: "0 0 16px" }}>
            Curated by TubeDealer Electronics Specialists
          </p>

          <p style={body}>
            TubeDealer is a premier marketplace and inventory tool for collectors, dealers, and
            musicians who care about the sound and history of modern and NOS vacuum tube gear.
            Find a hard-to-source valve for a studio amplifier, compare vintage audio components
            from established sellers, or organize an inherited collection in one dependable place.
            The platform brings discovery, pricing context, and practical stock control together
            without making you choose between a specialist shop and a useful inventory system.
          </p>

          <h3 style={heading}>For Dealers &amp; Collectors</h3>
          <p style={body}>
            Import supplier spreadsheets, scan markings, record condition and test notes, then
            manage asking prices from your phone or computer. A public storefront gives dealers
            a clear place to present matched pairs, tested stock, and carefully described NOS
            inventory. Collectors can keep private records until they are ready to share, while
            musicians can quickly identify the right replacement for a treasured piece of gear.
          </p>

          <h3 style={heading}>For Buyers</h3>
          <p style={body}>
            Browse listings from verified sellers by type, brand, grade, or price. Each listing
            is designed to make the important details easy to compare, from manufacturer and
            era to quantity and condition. Whether you need one replacement valve or a matched
            set for a full retube, TubeDealer helps you shop with more confidence and less
            guesswork.
          </p>
        </div>

        {/* Right column — FAQ */}
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: "#fff", margin: "0 0 16px" }}>
            Frequently Asked Questions
          </h2>

          <details style={faqItem}>
            <summary style={summary}>
              <span>How does TubeDealer sync inventory across platforms?</span>
              <span style={{ color: "#e8c070", flexShrink: 0, fontSize: 18, fontWeight: 400 }}>+</span>
            </summary>
            <p style={answer}>
              Start with a supplier CSV import or add stock through the inventory tool, then
              keep descriptions, quantities, prices, and listing status in one central record.
              TubeDealer can use that record to support multi-channel listing workflows, helping
              dealers publish consistent information without retyping every item for each
              marketplace.
            </p>
          </details>

          <details style={faqItem}>
            <summary style={summary}>
              <span>Can I buy individual vintage and NOS vacuum tubes without an account?</span>
              <span style={{ color: "#e8c070", flexShrink: 0, fontSize: 18, fontWeight: 400 }}>+</span>
            </summary>
            <p style={answer}>
              Yes. Guests can browse the marketplace, purchase individual items, and complete
              buyer checkout without creating a seller subscription. An account can still be
              useful for returning customers who want an easier way to track their activity,
              but it is not required just to shop for vintage or NOS inventory.
            </p>
          </details>

          <details style={faqItem}>
            <summary style={summary}>
              <span>What types of audio and industrial tubes are supported?</span>
              <span style={{ color: "#e8c070", flexShrink: 0, fontSize: 18, fontWeight: 400 }}>+</span>
            </summary>
            <p style={answer}>
              The catalog and workflow support common audio types such as 12AX7, 6L6, EL34,
              and 6V6, along with rectifiers, preamp and power valves, specialty industrial
              devices, and transmitter tubes. Markings, manufacturer details, and condition
              notes help you distinguish familiar studio and guitar-amp parts from less common
              industrial or broadcast components.
            </p>
          </details>
        </div>
      </div>
    </section>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function Home() {
  const { user } = useAuth();
  const [stats,   setStats]   = useState<MarketplaceStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getStats()
      .then(s => { setStats(s); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <SeoHead
        rawTitle={STATIC_PAGE_META["/"]!.title}
        description={STATIC_PAGE_META["/"]!.description}
        canonicalUrl={STATIC_PAGE_META["/"]!.canonical}
      />
      {user ? (
        <WelcomeDashboard user={user} />
      ) : (
        <div style={{
          background: "#0a0a0a",
          padding: "6px 20px 8px",
          textAlign: "center",
        }}>
          <h1 style={{ color: "#fff", fontSize: 22, fontWeight: 800, margin: 0 }}>
            Buy &amp; Sell Modern and Vintage Vacuum Tubes
          </h1>
        </div>
      )}

      <PanelSection stats={stats} />

      {/* ── About TubeDealer — semantic SEO section ────────────────────────── */}
      <HomeSeoSection />

      {/* ── Buy List demand ticker ─────────────────────────────────────────── */}
      <BuyListTicker />

      {/* ── Articles of Interest ───────────────────────────────────────────── */}
      <div style={{ borderTop: "1px solid #1a1a1a", marginTop: 8 }}>
        <ArticlesPanel />
      </div>
    </Layout>
  );
}
